import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ConfirmationMessageModal,
  EmailsTable,
  FullScreenLoader,
  MenuItem,
  ScrollToTop,
  TableActionsMenu,
} from '@Components/index';
import { CommonConstants } from '@Constants/index';
import EmailsModal from '@Containers/Emails/EmailsModal';
import { MetaData } from '@Models/common.models';
import { EmailsQuery } from '@Models/emails.models';
import {
  useArchiveEmailsMutation,
  useDeleteEmailsMutation,
  useGetEmailsListsQuery,
} from '@Services/email.service';
import {
  BaseAlertProps,
  ConfirmationActionTypes,
  DialerCrudTypes,
  DialerTypes,
} from '@Typings/global.types';
import {
  getFilters,
  GetRows,
  getRTKQuery,
  getModalBtnComponent,
  getConfirmationMessageNode,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

const EmailsArchive: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { triggerAlert } = useAlert();

  /** STATES */
  const [checked, setChecked] = useState<readonly string[]>([]);
  const [modalCrudType, setModalCrudType] = useState<DialerCrudTypes | null>(
    null
  );
  const [emailId, setEmailId] = useState<string>('');
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);

  const emailsArchivedQuery: EmailsQuery = useMemo(
    () => getRTKQuery(location.search),
    [location.search]
  );

  /** RTK QUERY HOOK */
  const {
    data,
    isLoading,
    isFetching,
    error: getEmailsError,
  } = useGetEmailsListsQuery({
    data: emailsArchivedQuery,
    isArchive: true,
  });
  const [archiveEmails, archiveMethods] = useArchiveEmailsMutation();
  const [deleteEmails, deleteMethods] = useDeleteEmailsMutation();

  /** TABLE LOADING STATE */
  const loading =
    isLoading ||
    isFetching ||
    archiveMethods.isLoading ||
    deleteMethods.isLoading;

  // show error for get query
  useEffect(() => {
    if (getEmailsError) {
      triggerAlert(getEmailsError, setAlertState);
    }
  }, [getEmailsError, triggerAlert]);

  /** UPDATES THE CHECKED STATE AFTER BULK ACTIONS */
  useEffect(() => {
    setEmailId('');
    setIsBulkAction(false);
    setItemsCount(0);

    if (archiveMethods.isSuccess) {
      setChecked([]);
    }

    if (deleteMethods.isSuccess) {
      setChecked([]);
    }
  }, [archiveMethods.isSuccess, deleteMethods.isSuccess]);

  /** TRIGGERS SUCCESS OR ERROR ALERT AFTER ACTIONS */
  useEffect(() => {
    // ARCHIVE SUCCESS
    if (archiveMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.UNARCHIVE_EMAIL,
        setAlertState
      );
    }

    // ARCHIVE ERROR
    if (archiveMethods.isError) {
      triggerAlert(archiveMethods.error, setAlertState);
    }
  }, [
    triggerAlert,
    archiveMethods.isSuccess,
    archiveMethods.isError,
    archiveMethods.error,
  ]);

  useEffect(() => {
    // DELETE SUCCESS
    if (deleteMethods.isSuccess) {
      triggerAlert(CommonConstants.GLOBAL_MESSAGES.DELETE_EMAIL, setAlertState);
    }

    // DELETE ERROR
    if (deleteMethods.error) {
      triggerAlert(deleteMethods.error, setAlertState);
    }
  }, [triggerAlert, deleteMethods.isSuccess, deleteMethods.error]);

  /** EMAILS ACTION HANDLERS --> */
  /** HANDLES EMAILS UNARCHIVE */
  const handleUnarchive = (ids: readonly string[]) => {
    const userId = query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';
    const metaData: MetaData = getFilters(query.parse(), true);

    return itemsCount
      ? archiveEmails({
          ...metaData,
          archive: false,
          itemsCount,
          userId: `${userId}`,
        })
      : archiveEmails({ ids, archive: false, userId: `${userId}` });
  };

  /** HANDLES EMAILS DELETE */
  const handleDelete = (ids?: readonly string[]) => {
    const userId = query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';
    const metaData: MetaData = getFilters(query.parse(), true);

    return itemsCount
      ? deleteEmails({ ...metaData, itemsCount, userId: `${userId}` })
      : deleteEmails({ ids, userId: `${userId}` });
  };
  /** <-- EMAILS ACTION HANDLERS */

  /** Table Modal Button Component Function
   *
   * Returns the Modal Button component which opens modal when clicked.
   */
  const modalBtnComponent = (id: string, name: string): JSX.Element => {
    const handleClick = () => {
      setModalCrudType(DialerCrudTypes.Archive);
      setEmailId(id);
    };

    return getModalBtnComponent({
      id,
      name,
      onClick: handleClick,
    });
  };

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (confirmationActionType === ConfirmationActionTypes.Delete) {
      return isBulkAction ? handleDelete(checked) : handleDelete([emailId]);
    }
    if (confirmationActionType === ConfirmationActionTypes.Unarchive) {
      return isBulkAction
        ? handleUnarchive(checked)
        : handleUnarchive([emailId]);
    }
  };

  /** Table Actions Component Function
   *
   * Returns the table actions component with required menu items.
   */
  const actionsComponent = (id: string, disabled?: boolean): JSX.Element => {
    /** Handles click of action menu items */
    const handleTableActionsClick = (
      confirmationType: ConfirmationActionTypes
    ) => {
      setIsConfirmationModalOpen(true);
      setConfirmationActionType(confirmationType);
    };

    /** Actions Menu Data */
    const actionsMenu: MenuItem[] = [
      {
        label: t('common.messages.Delete'),
        icon: 'delete',
        value: id,
        onClick: () => {
          setItemsCount(0);
          setIsBulkAction(false);
          setEmailId(id);
          handleTableActionsClick(ConfirmationActionTypes.Delete);
        },
      },
      {
        label: t('common.messages.Unarchive'),
        icon: 'unarchive',
        value: id,
        onClick: () => {
          setItemsCount(0);
          setIsBulkAction(false);
          setEmailId(id);
          handleTableActionsClick(ConfirmationActionTypes.Unarchive);
        },
      },
    ];

    return <TableActionsMenu menuItems={actionsMenu} disabled={disabled} />;
  };

  /** Rows, Total Rows Count */
  const rows = GetRows(data?.list, actionsComponent, modalBtnComponent);
  const totalRowsCount = data?.meta.count
    ? parseInt(data.meta.count, 10)
    : CommonConstants.INITIAL_ROWS_PER_PAGE;

  /** Handle Bulk Count
   * Callback to update ItemsCount
   */
  const handleBulk = (count: number) => setItemsCount(count);

  /** Handles Bulk action button click */
  const handleBulkActionBtnClick = (
    confirmationType: ConfirmationActionTypes
  ) => {
    setConfirmationActionType(confirmationType);
    setIsBulkAction(true);
    setIsConfirmationModalOpen(true);
  };

  return !isLoading ? (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <EmailsModal
        emailId={emailId}
        setEmailId={setEmailId}
        crudType={modalCrudType}
        setCrudType={setModalCrudType}
        unarchiveBtnOnClick={() => handleUnarchive([emailId])}
        deleteBtnOnClick={() => handleDelete([emailId])}
      />
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(confirmationActionType)}
        onClick={handleConfirmationModalClick}
      />
      <EmailsTable
        heading={query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) && <></>}
        tabChild={!!query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID)}
        selectedBtnNumber={itemsCount || checked.length}
        alertState={alertState}
        EmailTemplateType={DialerTypes.Archived}
        archiveUnarchiveBtnProps={{
          onClick: () => {
            handleBulkActionBtnClick(ConfirmationActionTypes.Unarchive);
          },
          disabled: loading,
        }}
        deleteBtnProps={{
          onClick: () => {
            handleBulkActionBtnClick(ConfirmationActionTypes.Delete);
          },
          disabled: loading,
        }}
        tableProps={{
          loading,
          rows,
          checked,
          setChecked,
          paginationProps: {
            count: totalRowsCount,
          },
        }}
        handleBulk={handleBulk}
      />
    </>
  ) : (
    <FullScreenLoader />
  );
};

export default EmailsArchive;
